import type { SmartBriefs, SmartBriefsWithContent } from "@unbox/api";
import { Anchor, AnnotationInfo, CheckCircleBroken, Colors, Sale03, Star06, Tag01 } from "@untitledui/icons";
import { AlertTriangle } from "lucide-react";
import { VideoEmbed } from "@/pages/videos/video-embed";

export type VideoExample = SmartBriefsWithContent["examples"][number] & {
  videoUrl?: string | null;
};

interface SmartBriefContentProps {
  brief: SmartBriefs & { productThumbnail?: string | null };
  content: SmartBriefsWithContent["content"] | undefined | null;
  examples?: VideoExample[];
  showCompanyLogo?: boolean;
  isPublic?: boolean;
  headerActions?: React.ReactNode;
  backButton?: React.ReactNode;
  titlePrefix?: React.ReactNode;
  bannerColor?: string;
}

export const BrandLogoAndBanner = ({
  bannerColor = "#F3F3F3",
  logoUrl,
  backButton,
  headerActions,
}: {
  bannerColor?: string;
  logoUrl: string | null | undefined;
  backButton?: React.ReactNode;
  headerActions?: React.ReactNode;
}) => {
  return (
    <div
      className="w-full self-stretch flex justify-center px-4 sm:px-6 md:px-10 pt-6 sm:pt-8 md:pt-12 pb-6 sm:pb-8 md:pb-12"
      style={{ backgroundColor: bannerColor }}
    >
      <div className="w-full max-w-[1168px] flex flex-col gap-6 sm:gap-8 md:gap-10">
        {/* Top Header with Action Buttons */}
        {(backButton || headerActions) && (
          <div className="self-stretch flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 flex-wrap">
            <div className="flex-1">{backButton}</div>
            {headerActions && <div className="flex items-center gap-2 flex-wrap">{headerActions}</div>}
          </div>
        )}

        {/* Logo Row - just the image, no container styling in view mode */}
        <div className="w-full flex justify-between items-center min-h-[48px]">
          {logoUrl && (
            <img src={logoUrl} alt="Brand logo" className="h-8 sm:h-12 w-auto max-w-32 sm:max-w-40 object-contain" />
          )}
        </div>
      </div>
    </div>
  );
};

export const CategoryIcon = ({ category }: { category: string }) => {
  switch (category) {
    case "Video Style":
    case "Video Style (Archetypes)":
      return <Colors className="h-6 w-6 text-gray-400" />;
    case "Hook":
      return <Anchor className="h-6 w-6 text-gray-400" />;
    case "Selling Points":
      return <Tag01 className="h-6 w-6 text-gray-400" />;
    case "Sales Tactics":
    case "Sales Tactics (Tactics)":
      return <Sale03 className="h-6 w-6 text-gray-400" />;
    default:
      return <AlertTriangle className="h-6 w-6 text-gray-400" />;
  }
};

// Guideline section showing both do and don't patterns inline
const GuidelineSection = ({ doItem, dontItem }: { doItem: any; dontItem?: any }) => {
  if (!doItem) return null;
  if (doItem.description === "No major insight for this category.") return null;
  if (doItem.description === "") return null;

  // Normalize category header for display
  const displayCategory = doItem.category_header.replace(" (Archetypes)", "").replace(" (Tactics)", "");

  const positivePatterns = doItem.key_patterns || [];
  const negativePatterns = dontItem?.key_patterns || [];

  return (
    <div className="w-full max-w-[768px] rounded-lg flex flex-col justify-start items-start gap-3 sm:gap-4">
      {/* Header with icon and title */}
      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        <div className="self-stretch inline-flex justify-start items-start gap-2">
          <CategoryIcon category={doItem.category_header} />
          <div className="justify-start text-quaternary text-sm sm:text-base font-medium leading-5 sm:leading-6">
            {displayCategory}
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="self-stretch inline-flex justify-start items-start gap-4 sm:gap-8">
        <div className="flex-1 inline-flex flex-col justify-start items-start gap-3 sm:gap-4">
          {/* Description */}
          <div className="self-stretch justify-start text-primary text-base sm:text-lg font-normal leading-6 sm:leading-7">
            {doItem.description}
          </div>

          {/* Tags - both positive and negative inline */}
          {(positivePatterns.length > 0 || negativePatterns.length > 0) && (
            <div className="self-stretch inline-flex justify-start items-start gap-1.5 flex-wrap content-start">
              {/* Positive (green/success) tags */}
              {positivePatterns.map((pattern: string, idx: number) => (
                <div
                  key={`pos-${idx}`}
                  className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-50 rounded-full outline outline-1 outline-offset-[-1px] outline-green-200 flex justify-start items-center"
                >
                  <div className="text-center justify-start text-green-700 text-xs sm:text-sm font-medium leading-4 sm:leading-5">
                    {pattern}
                  </div>
                </div>
              ))}
              {/* Negative (red/error) tags */}
              {negativePatterns.map((pattern: string, idx: number) => (
                <div
                  key={`neg-${idx}`}
                  className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-50 rounded-full outline outline-1 outline-offset-[-1px] outline-red-200 flex justify-start items-center"
                >
                  <div className="text-center justify-start text-red-700 text-xs sm:text-sm font-medium leading-4 sm:leading-5">
                    {pattern}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Horizontal divider - simple 1px line
const Divider = () => <div className="w-full h-px bg-gray-200" />;

export const SmartBriefContent = ({
  brief,
  content,
  examples = [],
  showCompanyLogo = false,
  isPublic = false,
  headerActions,
  backButton,
  titlePrefix,
  bannerColor = "#F3F3F3",
}: SmartBriefContentProps) => {
  // Helper to get dontList item matching a doList category
  // Handles both normalized (dontList) and raw (dont_list) data
  const getDontItemForCategory = (categoryHeader: string) => {
    const dontList = content?.dontList || (content as any)?.dont_list || [];
    return dontList.find((item: any) => item.category_header === categoryHeader);
  };

  // Get the doList (handles both normalized and raw data)
  const getDoList = () => {
    return content?.doList || (content as any)?.do_list || [];
  };

  return (
    <div className="w-full min-h-screen bg-white overflow-hidden">
      <div className="flex flex-col">
        {/* Header Section - Colored Background */}
        <BrandLogoAndBanner
          bannerColor={bannerColor}
          logoUrl={brief.logoUrl}
          backButton={backButton}
          headerActions={headerActions}
        />

        {/* Main Content Area */}
        <div className="w-full flex justify-center px-4 sm:px-6 md:px-10 py-8 sm:py-12 md:py-16">
          <div className="w-full max-w-[1168px] bg-white inline-flex flex-col justify-start items-center gap-12 sm:gap-16 md:gap-24 overflow-hidden">
            {/* Title, Date, Introduction Section - reverse order on mobile so image appears first */}
            <div className="self-stretch inline-flex flex-col-reverse md:flex-row justify-between items-start gap-6 md:gap-0">
              <div className="flex-1 w-full md:max-w-[768px] md:pr-16 inline-flex flex-col justify-start items-start gap-4 sm:gap-6 md:gap-8">
                <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                  {titlePrefix}
                  <div className="self-stretch justify-start text-primary text-2xl sm:text-3xl md:text-4xl font-normal leading-8 sm:leading-9 md:leading-10">
                    {brief.title}
                  </div>
                  <div className="justify-start text-placeholder text-sm sm:text-base font-semibold leading-5 sm:leading-6">
                    {new Date(brief.createdAt).toLocaleDateString("en-CA", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    })}
                  </div>
                </div>
                {content?.product && (
                  <div className="self-stretch justify-start text-tertiary text-base sm:text-lg font-normal leading-6 sm:leading-7">
                    {content.product}
                  </div>
                )}
              </div>
              {brief.productThumbnail && (
                <img
                  className="w-[120px] h-[120px] md:w-[218px] md:h-[218px] rounded-lg object-cover shrink-0"
                  src={brief.productThumbnail}
                  alt={brief.productName}
                />
              )}
            </div>

            {/* Divider - only show if there's content below */}
            {content && <Divider />}

            {/* Guidelines Section */}
            {content && (
              <div className="self-stretch inline-flex flex-col lg:flex-row justify-start items-start gap-6 lg:gap-6">
                {/* Left Sidebar */}
                <div className="w-full lg:w-96 inline-flex flex-col justify-start items-start gap-4 sm:gap-6 lg:gap-8">
                  <div className="w-full lg:w-72 justify-start text-primary text-lg sm:text-xl font-medium leading-7 sm:leading-8">
                    Guidelines for videos that connect and convert
                  </div>
                  <div className="inline-flex justify-start items-start gap-2">
                    <div className="self-stretch inline-flex flex-col justify-start items-center gap-2">
                      <Star06 className="w-5 h-5 text-gray-700 flex-shrink-0" />
                      <div className="w-1 flex-1 bg-quaternary rounded-md" />
                    </div>
                    <p className="w-full lg:w-72 justify-start text-secondary text-sm sm:text-base font-normal leading-5 sm:leading-6 italic">
                      We've studied thousands of successful videos to identify proven patterns that increase your
                      chances of sales.
                    </p>
                  </div>
                </div>

                {/* Right Content - Guidelines */}
                <div className="flex-1 w-full inline-flex flex-col justify-start items-start gap-8 sm:gap-10 lg:gap-12">
                  <div className="self-stretch inline-flex justify-start items-start gap-8 sm:gap-10 lg:gap-12">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-8 sm:gap-10 lg:gap-12">
                      {/* Do List Items with matching Don't patterns - ordered to match edit mode */}
                      {getDoList()
                        .sort((a: any, b: any) => {
                          // Define the desired order
                          const order = [
                            "Video Style",
                            "Video Style (Archetypes)",
                            "Hook",
                            "Selling Points",
                            "Sales Tactics",
                            "Sales Tactics (Tactics)",
                          ];
                          const aIndex = order.findIndex((o) => a.category_header?.includes(o.split(" ")[0]));
                          const bIndex = order.findIndex((o) => b.category_header?.includes(o.split(" ")[0]));
                          return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
                        })
                        .map((item: any, index: number, arr: any[]) => (
                          <div key={index} className="w-full flex flex-col gap-8 sm:gap-10 lg:gap-12">
                            <GuidelineSection doItem={item} dontItem={getDontItemForCategory(item.category_header)} />
                            {index < arr.length - 1 && <div className="w-full max-w-[700px] h-px bg-gray-200" />}
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Divider before examples */}
                  {examples.length > 0 && <div className="w-full max-w-[700px] h-px bg-gray-200" />}

                  {/* Good Examples */}
                  {examples.length > 0 && (
                    <div className="w-full max-w-[768px] rounded-lg flex flex-col justify-start items-start gap-4 sm:gap-5">
                      <div className="self-stretch flex flex-col justify-start items-start gap-2">
                        <div className="self-stretch inline-flex justify-start items-start gap-2">
                          <CheckCircleBroken className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                          <div className="justify-start text-quaternary text-sm sm:text-base font-medium leading-5 sm:leading-6">
                            Good examples
                          </div>
                        </div>
                      </div>
                      <div className="self-stretch flex-1 inline-flex justify-start items-start gap-4 sm:gap-6 lg:gap-8 flex-wrap content-start">
                        {examples.slice(0, 2).map((video: VideoExample) => (
                          <div
                            key={video.videoId}
                            className="w-full sm:w-auto sm:flex-1 sm:min-w-[200px] sm:max-w-[300px] inline-flex flex-col justify-start items-start gap-3 sm:gap-4"
                          >
                            <VideoEmbed url={video.videoUrl} thumbnail={video.thumbnail} />
                            <div className="self-stretch justify-start text-primary text-base sm:text-lg font-semibold leading-6 sm:leading-7">
                              @{video.creatorName}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Things to Avoid Section - bullet list format */}
            {content?.safetyList && (
              <>
                {/* Divider */}
                <Divider />

                <div className="self-stretch inline-flex flex-col lg:flex-row justify-start items-start gap-6">
                  {/* Left Sidebar */}
                  <div className="w-full lg:w-96 inline-flex flex-col justify-start items-start gap-4 sm:gap-6 lg:gap-8">
                    <div className="w-full lg:w-72 justify-start text-primary text-lg sm:text-xl font-medium leading-7 sm:leading-8">
                      Things to avoid in your videos
                    </div>
                    <div className="inline-flex justify-start items-start gap-2">
                      <div className="self-stretch inline-flex flex-col justify-start items-center gap-2">
                        <AnnotationInfo className="w-5 h-5 text-gray-700 flex-shrink-0" />
                        <div className="w-1 flex-1 bg-quaternary rounded-md" />
                      </div>
                      <p className="w-full lg:w-72 justify-start text-secondary text-sm sm:text-base font-normal leading-5 sm:leading-6 italic">
                        Complying with our brand safety standards increases the likelihood that your content will get
                        boosted
                      </p>
                    </div>
                  </div>

                  {/* Right Content - Bullet list */}
                  <div className="flex-1 w-full justify-start text-primary text-base sm:text-lg font-normal leading-6 sm:leading-7">
                    <ul className="list-disc list-inside space-y-1">
                      {content.safetyList
                        .split("\n")
                        .filter(Boolean)
                        .map((line: string, idx: number) => (
                          <li key={idx}>{line.trim()}</li>
                        ))}
                    </ul>
                  </div>
                </div>
              </>
            )}

            {/* More Info Section */}
            {content?.brief && (
              <>
                {/* Divider */}
                <Divider />

                <div className="self-stretch inline-flex flex-col lg:flex-row justify-start items-start gap-4 sm:gap-6 pb-24">
                  <div className="w-full lg:w-96 inline-flex flex-col justify-start items-start gap-4 sm:gap-6 lg:gap-8">
                    <div className="w-full lg:w-72 justify-start text-primary text-lg sm:text-xl font-medium leading-7 sm:leading-8">
                      More Info
                    </div>
                  </div>
                  <div className="flex-1 w-full justify-start text-primary text-base sm:text-lg font-normal leading-6 sm:leading-7 whitespace-pre-line">
                    {content.brief}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
